import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Configurer axios pour envoyer les cookies httpOnly
axios.defaults.withCredentials = true;

const AuthContext = createContext({
  user: null,
  loading: true,
  login: async () => {},
  logout: async () => {},
  refresh: async () => {},
});

export const useAuth = () => useContext(AuthContext);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/auth/me`);
      setUser(r.data);
      return r.data;
    } catch (e) {
      setUser(null);
      return null;
    }
  }, []);

  useEffect(() => {
    (async () => {
      await refresh();
      setLoading(false);
    })();
  }, [refresh]);

  // 🆕 Applique une classe globale `role-viewer` sur <body> pour permettre un
  // gating CSS read-only (cacher les boutons d'édition pour les viewers).
  useEffect(() => {
    if (!user) {
      document.body.classList.remove('role-viewer', 'role-commercial', 'role-admin');
      return;
    }
    document.body.classList.remove('role-viewer', 'role-commercial', 'role-admin');
    document.body.classList.add(`role-${(user.role || '').toLowerCase()}`);
  }, [user]);

  const login = useCallback(async (email, password) => {
    const r = await axios.post(`${API}/auth/login`, { email, password });
    setUser(r.data);
    // Compat avec ancien code qui check sessionStorage.crm_authenticated
    sessionStorage.setItem('crm_authenticated', 'true');
    sessionStorage.setItem('auth_timestamp', Date.now().toString());
    return r.data;
  }, []);

  const logout = useCallback(async () => {
    try { await axios.post(`${API}/auth/logout`); } catch (e) { /* ignore */ }
    setUser(null);
    sessionStorage.removeItem('crm_authenticated');
    sessionStorage.removeItem('auth_timestamp');
  }, []);

  return (
    <AuthContext.Provider value={{ user, loading, login, logout, refresh }}>
      {children}
    </AuthContext.Provider>
  );
};

// Hook utilitaire pour gating UI selon rôle
export const useRole = () => {
  const { user } = useAuth();
  const role = (user?.role || '').toLowerCase();
  return {
    isAdmin: role === 'admin',
    isCommercial: role === 'commercial',
    isViewer: role === 'viewer',
    canEdit: role === 'admin' || role === 'commercial',
    role,
  };
};

export default AuthContext;
